import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Container, Grid, Paper, Typography, Box, Card, 
    CardContent, CircularProgress, Button, Fade, Avatar, 
    Divider, IconButton,  Chip, Zoom 
} from '@mui/material';
import { 
    Class as ClassIcon, 
    Refresh as RefreshIcon,
    Group as GroupIcon,
    MenuBook as SubjectIcon,
    NotificationsActive as NoticeIcon,
    Assignment as HomeworkIcon,
    CheckCircle as AttendanceIcon,
    NoteAdd as NotesIcon,
    Grade as MarksIcon,
    CalendarMonth as CalendarIcon,
    TrendingUp as StatsIcon,
    ArrowForward as ArrowIcon,
    Lightbulb as TipsIcon
} from '@mui/icons-material';
import SeeNotice from '../../components/SeeNotice';
import CountUp from 'react-countup';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;
const float = keyframes`0% { transform: translateY(0px); } 50% { transform: translateY(-10px); } 100% { transform: translateY(0px); }`;
const shine = keyframes`0% { left: -100%; } 100% { left: 100%; }`;

// Styled Components
const PageWrapper = styled(Box)`
    background-color: #f8fafc;
    min-height: 100vh;
    padding-bottom: 60px;
    animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHero = styled(Box)`
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    padding: 80px 0 100px;
    color: white;
    position: relative;
    overflow: hidden;
    
    &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 800px;
        height: 800px;
        background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(30%, -30%);
    }

    &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
        border-radius: 50%;
        transform: translate(-30%, 30%);
    }
`;

const GlassCard = styled(Paper)`
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px) !important;
    border-radius: 24px !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.05) !important;
    overflow: hidden;
    height: 100%;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.1) !important;
    }
`;

const ActionCard = styled(Paper)`
    padding: 24px;
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;
    background: white;
    
    &:hover {
        transform: translateY(-8px);
        background: #f8fafc;
        border-color: ${props => props.color || '#6366f1'};
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        
        .icon-box {
            transform: scale(1.1) rotate(5deg);
        }
    }
`;

const IconBox = styled(Box)`
    width: 60px;
    height: 60px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.bgcolor || '#eef2ff'};
    color: ${props => props.color || '#6366f1'};
    transition: all 0.3s ease;
`;

const StatBadge = styled(Box)`
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 4px;
    background: ${props => props.bgcolor || '#f1f5f9'};
    color: ${props => props.color || '#475569'};
`;

const TeacherHomePage = () => {
    const { currentUser } = useSelector((state) => state.user);
    const [teacherClasses, setTeacherClasses] = useState([]);
    const [homeworkCount, setHomeworkCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const navigate = useNavigate();

    const teacherId = currentUser?._id;

    const fetchData = useCallback(async () => {
        setRefreshing(true);
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            // Fetch Classes
            const classRes = await axios.get(`${apiUrl}/Teacher/Classes/${teacherId}`);
            if (Array.isArray(classRes.data)) {
                setTeacherClasses(classRes.data);
            }

            // Fetch Homework for counts
            const hwRes = await axios.get(`${apiUrl}/Homework/${teacherId}`);
            if (Array.isArray(hwRes.data)) {
                setHomeworkCount(hwRes.data.length);
            }

        } catch (err) {
            console.error('Error fetching home data:', err);
        }
        setLoading(false);
        setRefreshing(false);
    }, [teacherId]);

    useEffect(() => {
        if (teacherId) {
            fetchData();
        }
    }, [teacherId, fetchData]);

    const totalStudents = useMemo(() => {
        // This is an estimate based on subjects/classes, normally we'd need a specific endpoint
        return teacherClasses.length * 25; // Placeholder multiplier
    }, [teacherClasses]);

    const shortcuts = [
        { title: 'Take Attendance', icon: <AttendanceIcon />, path: '/Teacher/attendance', color: '#10b981', bgcolor: '#f0fdf4' },
        { title: 'Upload Notes', icon: <NotesIcon />, path: '/Teacher/notes', color: '#6366f1', bgcolor: '#eef2ff' },
        { title: 'Assign Homework', icon: <HomeworkIcon />, path: '/Teacher/homework', color: '#f59e0b', bgcolor: '#fff7ed' },
        { title: 'Manage Marks', icon: <MarksIcon />, path: '/Teacher/marks', color: '#ef4444', bgcolor: '#fef2f2' },
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={60} thickness={4} sx={{ color: '#6366f1' }} />
            </Box>
        );
    }

    return (
        <PageWrapper>
            <PremiumHero>
                <Container maxWidth="lg">
                    <Fade in timeout={1000}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Avatar 
                                        sx={{ 
                                            width: 80, 
                                            height: 80, 
                                            border: '4px solid rgba(255,255,255,0.2)',
                                            fontSize: '2rem',
                                            bgcolor: '#6366f1'
                                        }}
                                    >
                                        {currentUser?.name?.charAt(0)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em' }}>
                                            Welcome back, {currentUser?.name?.split(' ')[0]}!
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, opacity: 0.8 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 400 }}>
                                                Teacher Portal Overview
                                            </Typography>
                                            <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)', height: 20 }} />
                                            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CalendarIcon fontSize="small" /> {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: { md: 'flex-end' } }}>
                                    <GlassCard sx={{ p: 2, background: 'rgba(255,255,255,0.1) !important', border: '1px solid rgba(255,255,255,0.2) !important', color: 'white', minWidth: 140 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.7 }}>CLASSES</Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 900 }}><CountUp end={teacherClasses.length} /></Typography>
                                    </GlassCard>
                                    <GlassCard sx={{ p: 2, background: 'rgba(255,255,255,0.1) !important', border: '1px solid rgba(255,255,255,0.2) !important', color: 'white', minWidth: 140 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.7 }}>HOMEWORK</Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 900 }}><CountUp end={homeworkCount} /></Typography>
                                    </GlassCard>
                                </Box>
                            </Grid>
                        </Grid>
                    </Fade>
                </Container>
            </PremiumHero>

            <Container maxWidth="lg" sx={{ mt: -6 }}>
                <Grid container spacing={4}>
                    {/* Shortcuts Section */}
                    <Grid item xs={12}>
                        <Grid container spacing={3}>
                            {shortcuts.map((item, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                    <Zoom in timeout={500 + index * 100}>
                                        <ActionCard color={item.color} onClick={() => navigate(item.path)}>
                                            <IconBox className="icon-box" bgcolor={item.bgcolor} color={item.color}>
                                                {item.icon}
                                            </IconBox>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                {item.title}
                                            </Typography>
                                        </ActionCard>
                                    </Zoom>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Classes Grid */}
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <StatsIcon sx={{ color: '#6366f1' }} /> My Assigned Classes
                            </Typography>
                            <IconButton onClick={fetchData} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                                <RefreshIcon className={refreshing ? 'spin-animation' : ''} />
                            </IconButton>
                        </Box>
                        
                        <Grid container spacing={3}>
                            {teacherClasses.map((cls, index) => (
                                <Grid item xs={12} sm={6} key={cls._id}>
                                    <Fade in timeout={800 + index * 100}>
                                        <GlassCard onClick={() => navigate(`/Teacher/class/${cls._id}`)} sx={{ cursor: 'pointer' }}>
                                            <Box sx={{ p: 3 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                                    <Avatar sx={{ bgcolor: '#eef2ff', color: '#6366f1', borderRadius: '12px' }}>
                                                        <ClassIcon />
                                                    </Avatar>
                                                    <StatBadge bgcolor="#f0fdf4" color="#10b981">
                                                        <AttendanceIcon sx={{ fontSize: 14 }} /> ACTIVE
                                                    </StatBadge>
                                                </Box>
                                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                    Class {cls.sclassName}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, color: '#64748b' }}>
                                                        <SubjectIcon sx={{ fontSize: 14 }} /> {cls.subjects?.length || 0} Subjects
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, color: '#64748b' }}>
                                                        <GroupIcon sx={{ fontSize: 14 }} /> Students List
                                                    </Typography>
                                                </Box>
                                                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {cls.subjects?.slice(0, 2).map((sub) => (
                                                        <Chip key={sub._id} label={sub.subName} size="small" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }} />
                                                    ))}
                                                    {(cls.subjects?.length > 2) && (
                                                        <Chip label={`+${cls.subjects.length - 2} more`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                                                    )}
                                                </Box>
                                            </Box>
                                        </GlassCard>
                                    </Fade>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Notice Board & Side Info */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <NoticeIcon sx={{ color: '#ef4444' }} /> Announcements
                            </Typography>
                            <GlassCard sx={{ p: 0 }}>
                                <SeeNotice />
                            </GlassCard>
                        </Box> 
                    </Grid>
                </Grid>
            </Container>

            <style>
                {`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spin-animation {
                    animation: spin 1s linear infinite;
                }
                `}
            </style>
        </PageWrapper>
    );
};

export default TeacherHomePage;
